import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { Icons } from '../Icons';

const EASE = [0.22, 1, 0.36, 1];
const SANS = '"Geist", "Inter", system-ui, sans-serif';
const MONO = '"Geist Mono", ui-monospace, monospace';
const GRAIN_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`;

const BRAND   = '#5A38FD';
const BRAND_2 = '#7F00FF';
const BRAND_L = '#a78bfa'; // light purple for text

const faqs = [
  { q: 'Is NILVISION free to start?', a: 'Yes. Create an account and use core tools free — no credit card required. Upgrade any time for full Contract Analyzer access, Ghost Agent, and unlimited scheduling.' },
  { q: 'Do I need a credit card to try it?', a: "No card required to start. When you're ready to upgrade, it takes one click inside the app." },
  { q: 'Is my data safe?', a: 'Bank-level encryption end-to-end, official Instagram Graph API (no password sharing), and secure AWS infrastructure to protect your contracts and earnings data.' },
  { q: 'Who is NILVISION built for?', a: 'College athletes navigating NIL deals — from first-time brand partnerships to multi-deal contracts. Built by former student-athletes who know the terrain.' },
];

const FEATURES = [
  { icon: Icons.Contract, color: BRAND_L, title: 'Contract Analyzer',  desc: 'Upload any NIL contract. Get red flags, risk score, and plain-English fixes in seconds.',               bullets: ['Red-flag & exclusivity detection', 'Deal Safety Score™', 'Compensation gap analysis'] },
  { icon: Icons.Ghost,    color: BRAND_L, title: 'Ghost Agent',         desc: 'Your AI negotiation coach. Submit a deal brief and get counter-offers, strategy, and leverage.',         bullets: ['Counter-offer suggestions', 'Deal evaluation & strategy', 'Contract brief generation'] },
  { icon: Icons.Scanner,  color: BRAND_L, title: 'Social Scanner',      desc: 'Catch compliance risks before they cost you eligibility or a sponsorship.',                              bullets: ['Real-time post risk scoring', 'Alcohol, gambling, controversy flags', 'NIL compliance reports'] },
  { icon: Icons.Content,  color: BRAND_L, title: 'Post Scheduler',      desc: 'Generate brand-safe captions and schedule posts via the official Instagram API.',                        bullets: ['Platform-specific captions', 'Calendar scheduling', 'Official Instagram API only'] },
  { icon: Icons.Star,     color: BRAND_L, title: 'NIL Score',            desc: 'See your market value score (0–100) and track growth over time.',                                       bullets: ['Dynamic 0–100 scoring', 'Tier system: Rookie → Elite', 'Personalized improvement tips'] },
  { icon: Icons.Earnings, color: BRAND_L, title: 'Earnings Tracker',    desc: 'Log deals, track payouts, and watch your cumulative earnings grow.',                                     bullets: ['Deal timeline tracking', 'Missing contract alerts', 'Earnings sparkline chart'] },
];

const ATHLETES = [
  { name: 'Hayden Schwartz',     school: 'UMASS · Football',               ig: 'schwartz_hayden',       img: 'Hayden+Schawartz.PNG' },
  { name: 'Luther McCoy',        school: 'Univ. of Hawaii · Football',      ig: 'luther__mccoy',         img: 'Luther+McCoy.JPEG' },
  { name: 'Jalen Logan-Redding', school: 'Minnesota · Football',            ig: 'jay.lr',                img: 'Jalen+Logan-Redding.JPEG' },
  { name: 'Muneer Newton',       school: 'Campbell · Basketball',           ig: 'muneer._11',            img: 'Muneer+Newton.JPEG' },
  { name: 'Dean Shepherd',       school: 'Delaware State · Basketball',     ig: '_dshepp',               img: 'Dean+Shepherd.JPEG' },
  { name: 'Teddy Pendergrass III', school: 'Delaware State · Basketball',   ig: 'teddy_pendergrass.3',  img: 'Teddy+Pendergrass+III.JPEG' },
  { name: 'Luke Gallo',          school: 'West Warwick HS · Football',      ig: 'luke_gallo9',           img: 'Luke+Gallo.JPEG' },
  { name: 'Tariq Watson',        school: 'UAB · Football',                  ig: 'twatson.24',            img: 'Tariq+Watson.JPEG' },
];


// ── Spotlight card (cursor-tracked glow) ─────────────────────────────────────
function SpotlightCard({ color = BRAND, children, style, onClick }) {
  const ref = useRef(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [hov, setHov] = useState(false);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setMouse({ x: e.clientX - r.left, y: e.clientY - r.top });
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={onClick}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.18 }}
      style={{ position: 'relative', overflow: 'hidden', borderRadius: 18, cursor: onClick ? 'pointer' : 'default', ...style }}
    >
      {/* Grain */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.04, mixBlendMode: 'overlay', backgroundImage: GRAIN_SVG, backgroundSize: '120px 120px', pointerEvents: 'none', zIndex: 0, borderRadius: 18 }} />
      {/* Cursor spotlight */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, borderRadius: 18,
        opacity: hov ? 1 : 0, transition: 'opacity 300ms ease',
        background: `radial-gradient(circle at ${mouse.x}px ${mouse.y}px, color-mix(in srgb, ${color} 16%, transparent) 0%, transparent 60%)`,
      }} />
      {/* Top hairline */}
      <div style={{
        position: 'absolute', top: 0, left: '12%', right: '12%', height: 1,
        background: `linear-gradient(90deg, transparent, color-mix(in srgb, ${color} 55%, transparent), transparent)`,
        pointerEvents: 'none', zIndex: 1,
        opacity: hov ? 1 : 0.5, transition: 'opacity 300ms ease',
      }} />
      <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>{children}</div>
    </motion.div>
  );
}

// ── Buttons ───────────────────────────────────────────────────────────────────
function PrimaryBtn({ children, onClick, large }) {
  const [hov, setHov] = useState(false);
  return (
    <motion.button whileTap={{ scale: 0.96 }}
      onHoverStart={() => setHov(true)} onHoverEnd={() => setHov(false)}
      onClick={onClick}
      style={{
        position: 'relative', overflow: 'hidden',
        padding: large ? '14px 34px' : '10px 24px', borderRadius: 12, border: 'none', cursor: 'pointer',
        fontSize: large ? '1rem' : '0.875rem', fontWeight: 700, color: '#fff',
        background: `linear-gradient(135deg, ${BRAND}, ${BRAND_2})`,
        boxShadow: hov ? '0 8px 28px rgba(90,56,253,0.6)' : '0 4px 16px rgba(90,56,253,0.35)',
        transform: hov ? 'translateY(-1px)' : 'translateY(0)',
        transition: 'box-shadow 180ms ease, transform 180ms ease',
        fontFamily: SANS,
      }}
    >
      {/* Shimmer sweep on hover */}
      <motion.div
        initial={{ x: '-100%', skewX: -15 }}
        animate={hov ? { x: '200%' } : { x: '-100%' }}
        transition={{ duration: 0.55, ease: 'easeOut' }}
        style={{ position: 'absolute', top: 0, bottom: 0, width: '40%', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent)', pointerEvents: 'none' }}
      />
      {children}
    </motion.button>
  );
}

function GhostBtn({ children, onClick, large }) {
  const [hov, setHov] = useState(false);
  return (
    <motion.button whileTap={{ scale: 0.96 }}
      onHoverStart={() => setHov(true)} onHoverEnd={() => setHov(false)}
      onClick={onClick}
      style={{
        padding: large ? '14px 28px' : '10px 22px', borderRadius: 12, cursor: 'pointer',
        fontSize: large ? '1rem' : '0.875rem', fontWeight: 600,
        color: hov ? '#fff' : 'rgba(255,255,255,0.55)',
        background: hov ? 'rgba(255,255,255,0.05)' : 'transparent',
        border: `1px solid ${hov ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.18)'}`,
        transition: 'border-color 160ms, color 160ms, background 160ms',
        fontFamily: SANS,
      }}
    >{children}</motion.button>
  );
}

// ── Check mark (brand purple — semantic "included") ───────────────────────────
function CheckMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
      <circle cx="7" cy="7" r="7" fill="rgba(90,56,253,0.18)" />
      <path d="M4 7l2 2 4-4" stroke={BRAND_L} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Floating orbs ─────────────────────────────────────────────────────────────
function FloatingOrbs() {
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      {/* Main glow */}
      <motion.div
        animate={{ scale: [1, 1.08, 1], opacity: [0.18, 0.26, 0.18] }}
        transition={{ repeat: Infinity, duration: 7, ease: 'easeInOut' }}
        style={{ position: 'absolute', top: -200, left: '50%', transform: 'translateX(-50%)', width: 900, height: 700, background: `radial-gradient(ellipse at center, rgba(90,56,253,0.28) 0%, transparent 68%)`, borderRadius: '50%' }}
      />
      {/* Orb 1 */}
      <motion.div
        animate={{ y: [0, -28, 0], x: [0, 12, 0], opacity: [0.12, 0.2, 0.12] }}
        transition={{ repeat: Infinity, duration: 9, ease: 'easeInOut' }}
        style={{ position: 'absolute', top: '15%', left: '12%', width: 280, height: 280, background: `radial-gradient(circle, rgba(90,56,253,0.22) 0%, transparent 70%)`, borderRadius: '50%', filter: 'blur(40px)' }}
      />
      {/* Orb 2 */}
      <motion.div
        animate={{ y: [0, 22, 0], x: [0, -15, 0], opacity: [0.1, 0.18, 0.1] }}
        transition={{ repeat: Infinity, duration: 11, ease: 'easeInOut', delay: 2 }}
        style={{ position: 'absolute', top: '20%', right: '10%', width: 320, height: 320, background: `radial-gradient(circle, rgba(127,0,255,0.2) 0%, transparent 70%)`, borderRadius: '50%', filter: 'blur(50px)' }}
      />
      {/* Orb 3 — small blue accent */}
      <motion.div
        animate={{ y: [0, -18, 0], x: [0, 20, 0], opacity: [0.08, 0.15, 0.08] }}
        transition={{ repeat: Infinity, duration: 13, ease: 'easeInOut', delay: 4 }}
        style={{ position: 'absolute', bottom: '10%', left: '30%', width: 200, height: 200, background: `radial-gradient(circle, rgba(96,165,250,0.18) 0%, transparent 70%)`, borderRadius: '50%', filter: 'blur(35px)' }}
      />
    </div>
  );
}

// ── FAQ accordion ─────────────────────────────────────────────────────────────
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      whileHover={{ borderColor: 'rgba(90,56,253,0.25)' }}
      style={{ background: '#0F0F17', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, overflow: 'hidden', cursor: 'pointer' }}
      onClick={() => setOpen(o => !o)}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', gap: 12 }}>
        <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)', lineHeight: 1.4 }}>{q}</span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.22, ease: EASE }} style={{ flexShrink: 0, color: open ? BRAND_L : 'rgba(255,255,255,0.25)' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
        </motion.div>
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} transition={{ duration: 0.28, ease: EASE }} style={{ overflow: 'hidden' }}>
            <p style={{ margin: 0, padding: '0 18px 16px', fontSize: '0.82rem', color: 'rgba(255,255,255,0.38)', lineHeight: 1.65 }}>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Eyebrow label ─────────────────────────────────────────────────────────────
function Eyebrow({ children }) {
  return <p style={{ fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.13em', color: 'rgba(167,139,250,0.6)', textTransform: 'uppercase', fontFamily: MONO, margin: '0 0 12px' }}>{children}</p>;
}

// ─────────────────────────────────────────────────────────────────────────────

const LandingPage = ({ onShowAuth }) => {
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  // Subtle hero parallax — headline floats up slightly as you scroll
  const heroY = useTransform(scrollY, [0, 400], [0, -40]);

  const handleAuth = (tier = null, tab = 'signup') => {
    if (onShowAuth) onShowAuth(tier, tab);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#08080D', color: '#fff', fontFamily: SANS, overflowX: 'hidden' }}>
      <style>{`
        .lp-grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
        @media (max-width: 900px) { .lp-grid-4 { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 640px) { .lp-grid-4 { grid-template-columns: repeat(2, 1fr); gap: 10px; } }
        .lp-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
        @media (max-width: 880px) { .lp-grid-3 { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 540px) { .lp-grid-3 { grid-template-columns: 1fr; } }
        .lp-plans { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; align-items: stretch; }
        @media (max-width: 760px) { .lp-plans { grid-template-columns: 1fr; gap: 20px; } }
      `}</style>

      {/* ── Nav ── */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 60,
        background: 'rgba(8,8,13,0.85)', backdropFilter: 'blur(22px)', WebkitBackdropFilter: 'blur(22px)',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 58 }}>
          <motion.div initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, ease: EASE }}
            style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/nilvision-logo-gradient-background.png" alt="NILVision" style={{ width: 30, height: 30, borderRadius: 8 }} />
            <span style={{ fontSize: '1.1rem', fontWeight: 800, letterSpacing: '-0.02em', background: 'linear-gradient(135deg, #fff 0%, #c4b5fd 55%, #93c5fd 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>NILVISION</span>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, ease: EASE }}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <GhostBtn onClick={() => handleAuth(null, 'signin')}>Sign In</GhostBtn>
            <PrimaryBtn onClick={() => handleAuth(null, 'signup')}>Get Started</PrimaryBtn>
          </motion.div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section ref={heroRef} style={{ position: 'relative', overflow: 'hidden', minHeight: '92vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <FloatingOrbs />
        {/* Grain */}
        <div style={{ position: 'absolute', inset: 0, opacity: 0.035, mixBlendMode: 'overlay', backgroundImage: GRAIN_SVG, backgroundSize: '120px 120px', pointerEvents: 'none' }} />
        {/* Subtle grid */}
        <div style={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px)', backgroundSize: '40px 40px', pointerEvents: 'none' }} />

        <motion.div style={{ y: heroY, position: 'relative', maxWidth: 860, margin: '0 auto', padding: 'clamp(80px, 12vh, 130px) 20px clamp(60px, 8vh, 90px)', textAlign: 'center' }}>

          {/* Animated badge */}
          <motion.div
            initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: EASE }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 14px 5px 8px', borderRadius: 999, background: 'rgba(90,56,253,0.12)', border: '1px solid rgba(90,56,253,0.3)', fontSize: '0.75rem', fontWeight: 700, color: BRAND_L, letterSpacing: '0.02em', marginBottom: 28, fontFamily: MONO }}
          >
            <motion.span
              animate={{ opacity: [1, 0.35, 1], scale: [1, 1.25, 1] }}
              transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
              style={{ width: 7, height: 7, borderRadius: '50%', background: BRAND, display: 'inline-block', boxShadow: `0 0 8px ${BRAND}` }}
            />
            AI-Powered NIL Platform
          </motion.div>

          {/* Headline with staggered words */}
          <div style={{ overflow: 'hidden', marginBottom: 22 }}>
            <motion.h1
              initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.85, ease: EASE, delay: 0.08 }}
              style={{ fontSize: 'clamp(2.5rem, 7.5vw, 4.8rem)', fontWeight: 900, letterSpacing: '-0.035em', lineHeight: 1.04, margin: 0,
                background: 'linear-gradient(150deg, #fff 0%, #e0d7ff 45%, #c4b5fd 75%, #93c5fd 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}
            >
              Your AI NIL Agent.<br />Built to Get You Paid, Safely.
            </motion.h1>
          </div>

          {/* Subtext */}
          <motion.p
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, ease: EASE, delay: 0.22 }}
            style={{ fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', color: 'rgba(255,255,255,0.42)', maxWidth: 560, margin: '0 auto 36px', lineHeight: 1.65 }}
          >
            Analyze contracts, flag risks, grow your brand, and track earnings — all in one place.
            <span style={{ color: 'rgba(255,255,255,0.45)' }}> Built by former student-athletes.</span>
          </motion.p>

          {/* Value props */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.32, duration: 0.5 }}
            style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px 22px', marginBottom: 44 }}
          >
            {['No credit card to start', 'Official Instagram API', 'Bank-level encryption'].map((v, i) => (
              <motion.div key={v} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.38 + i * 0.07, duration: 0.4 }}
                style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)' }}>
                <motion.span
                  animate={{ opacity: [0.6, 1, 0.6] }}
                  transition={{ repeat: Infinity, duration: 2.5 + i * 0.5, ease: 'easeInOut', delay: i * 0.4 }}
                  style={{ width: 6, height: 6, background: BRAND_L, borderRadius: '50%', flexShrink: 0, boxShadow: `0 0 6px ${BRAND}` }}
                />
                {v}
              </motion.div>
            ))}
          </motion.div>

          {/* CTAs */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.38, duration: 0.5 }}
            style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
            <PrimaryBtn large onClick={() => handleAuth(null, 'signup')}>Start Free →</PrimaryBtn>
            <GhostBtn large onClick={() => document.getElementById('lp-plans')?.scrollIntoView({ behavior: 'smooth' })}>Compare Plans</GhostBtn>
          </motion.div>
        </motion.div>
      </section>

      {/* ── Athletes ── */}
      <section style={{ padding: '80px 20px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55, ease: EASE }}
            style={{ textAlign: 'center', marginBottom: 44 }}>
            <Eyebrow>Trusted by</Eyebrow>
            <h2 style={{ fontSize: 'clamp(1.6rem, 4vw, 2.2rem)', fontWeight: 800, letterSpacing: '-0.025em', color: '#fff', margin: 0 }}>Real Athletes. Real Results.</h2>
          </motion.div>

          <div className="lp-grid-4">
            {ATHLETES.map((a, i) => (
              <motion.div key={a.ig} initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-20px' }} transition={{ duration: 0.45, ease: EASE, delay: (i % 4) * 0.06 }}>
                <SpotlightCard color={BRAND} style={{ background: '#0F0F17', border: '1px solid rgba(255,255,255,0.07)', padding: 12 }}>
                  <div style={{ aspectRatio: '1', borderRadius: 10, overflow: 'hidden', marginBottom: 10, background: 'rgba(90,56,253,0.08)' }}>
                    <img src={`https://nilvision-ui.s3.us-west-2.amazonaws.com/players/${a.img}`} alt={a.name} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 300ms ease' }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'} />
                  </div>
                  <h4 style={{ fontWeight: 700, color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem', margin: '0 0 2px' }}>{a.name}</h4>
                  <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.28)', margin: '0 0 6px' }}>{a.school}</p>
                  <a href={`https://instagram.com/${a.ig}`} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.7rem', color: '#E1306C', textDecoration: 'none', fontWeight: 600 }}>
                    <Icons.Instagram style={{ width: 11, height: 11 }} />@{a.ig}
                  </a>
                </SpotlightCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section style={{ padding: '20px 20px 80px', borderTop: '1px solid rgba(255,255,255,0.05)', background: '#0A0A12' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', paddingTop: 60 }}>
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, ease: EASE }}
            style={{ textAlign: 'center', marginBottom: 48 }}>
            <Eyebrow>How it works</Eyebrow>
            <h2 style={{ fontSize: 'clamp(1.6rem, 4vw, 2.2rem)', fontWeight: 800, letterSpacing: '-0.025em', color: '#fff', margin: '0 0 10px' }}>Three Steps. Immediate Value.</h2>
            <p style={{ color: 'rgba(255,255,255,0.32)', fontSize: '1rem', margin: 0 }}>From contract upload to confident signing in minutes.</p>
          </motion.div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, alignItems: 'stretch' }}>
            {[
              { n: '01', icon: Icons.Contract, title: 'Upload a contract',  desc: 'We flag red-flags, exclusivity traps, and compensation gaps in seconds. Get a plain-English risk score.' },
              { n: '02', icon: Icons.Ghost,    title: 'Get AI strategy',    desc: 'Ghost Agent analyzes your deal and gives you counter-offer strategies, leverage points, and negotiation scripts.' },
              { n: '03', icon: Icons.Earnings, title: 'Track your growth',  desc: 'Log deals, watch cumulative earnings, and track your NIL Score as your brand grows.' },
            ].map((s, i) => (
              <motion.div key={s.n} initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.48, ease: EASE, delay: i * 0.1 }} style={{ height: '100%' }}>
                <SpotlightCard color={BRAND_L} style={{ background: '#0F0F17', border: '1px solid rgba(255,255,255,0.07)', padding: '22px 20px', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      style={{ width: 42, height: 42, borderRadius: 12, background: `color-mix(in srgb, ${BRAND_L} 14%, rgba(0,0,0,0))`, border: `1px solid color-mix(in srgb, ${BRAND_L} 25%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <s.icon style={{ width: 19, height: 19, color: BRAND_L }} />
                    </motion.div>
                    <span style={{ fontFamily: MONO, fontSize: '0.62rem', fontWeight: 800, color: 'rgba(255,255,255,0.13)', letterSpacing: '0.06em' }}>{s.n}</span>
                  </div>
                  <h3 style={{ fontSize: '0.98rem', fontWeight: 700, color: '#fff', margin: '0 0 8px' }}>{s.title}</h3>
                  <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)', margin: 0, lineHeight: 1.65, flex: 1 }}>{s.desc}</p>
                </SpotlightCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Always On ── */}
      <section style={{ padding: '80px 20px', background: '#08080D', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, ease: EASE }}
            style={{ textAlign: 'center', marginBottom: 52 }}>
            <Eyebrow>Agentic Platform · Always On</Eyebrow>
            <h2 style={{ fontSize: 'clamp(1.6rem, 4vw, 2.2rem)', fontWeight: 800, letterSpacing: '-0.025em', color: '#fff', margin: '0 0 12px' }}>
              NILVISION Works While You Practice
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.32)', fontSize: '1rem', margin: '0 auto', maxWidth: 520, lineHeight: 1.6 }}>
              No reminders needed. The moment something happens with your deals, we're already on it.
            </p>
          </motion.div>

          {/* Pulse line connector — decorative */}
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                {
                  icon: Icons.Contract,
                  label: 'When you upload a contract',
                  desc: 'A full negotiation strategy is ready before you even open the chat — leverage points, red flags, and counter-offer suggestions waiting for you.',
                },
                {
                  icon: Icons.Star,
                  label: 'Every night at 2 AM',
                  desc: 'Your NIL Score is recalculated across every deal, post, and contract. Wake up knowing exactly where you stand and what moved.',
                },
                {
                  icon: Icons.Bell,
                  label: 'Before a contract deadline',
                  desc: 'We track every expiration date on file and send you alerts at 90, 30, and 7 days out — before you even think to check.',
                },
                {
                  icon: Icons.Earnings,
                  label: 'When you log a deal',
                  desc: "Every deal you record gets checked for a matching contract. If something's missing, we flag it so you're never exposed.",
                },
                {
                  icon: Icons.Shield,
                  label: 'Any time your data changes',
                  desc: 'Your compliance report regenerates automatically — contracts, deals, and earnings formatted for your school, always up to date.',
                },
              ].map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: i % 2 === 0 ? -18 : 18 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-20px' }}
                  transition={{ duration: 0.48, ease: EASE, delay: i * 0.07 }}
                >
                  <SpotlightCard color={BRAND_L} style={{ background: '#0F0F17', border: '1px solid rgba(255,255,255,0.07)', padding: '20px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18 }}>
                      {/* Icon + pulse dot */}
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: `color-mix(in srgb, ${BRAND_L} 14%, rgba(0,0,0,0))`, border: `1px solid color-mix(in srgb, ${BRAND_L} 25%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <item.icon style={{ width: 19, height: 19, color: BRAND_L }} />
                        </div>
                        <motion.div
                          animate={{ scale: [1, 1.6, 1], opacity: [0.7, 0, 0.7] }}
                          transition={{ repeat: Infinity, duration: 2.4 + i * 0.3, ease: 'easeInOut', delay: i * 0.5 }}
                          style={{ position: 'absolute', top: -3, right: -3, width: 9, height: 9, borderRadius: '50%', background: BRAND_L, boxShadow: `0 0 6px ${BRAND_L}` }}
                        />
                      </div>
                      {/* Text */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: '0 0 4px', fontSize: '0.68rem', fontWeight: 700, color: BRAND_L, textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: MONO }}>{item.label}</p>
                        <p style={{ margin: 0, fontSize: '0.88rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.65 }}>{item.desc}</p>
                      </div>
                    </div>
                  </SpotlightCard>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section style={{ padding: '80px 20px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, ease: EASE }}
            style={{ textAlign: 'center', marginBottom: 44 }}>
            <Eyebrow>Features</Eyebrow>
            <h2 style={{ fontSize: 'clamp(1.6rem, 4vw, 2.2rem)', fontWeight: 800, letterSpacing: '-0.025em', color: '#fff', margin: '0 0 10px' }}>Everything You Need to Win</h2>
            <p style={{ color: 'rgba(255,255,255,0.32)', margin: 0 }}>Professional-grade NIL tools built for student-athletes.</p>
          </motion.div>

          <div className="lp-grid-3">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-20px' }} transition={{ duration: 0.42, ease: EASE, delay: (i % 3) * 0.09 }}>
                <SpotlightCard color={f.color} style={{ background: '#0F0F17', border: '1px solid rgba(255,255,255,0.07)', padding: '24px 22px', height: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <motion.div whileHover={{ scale: 1.08, rotate: -3 }} transition={{ duration: 0.18 }}
                    style={{ width: 46, height: 46, borderRadius: 13, background: `color-mix(in srgb, ${f.color} 14%, rgba(0,0,0,0))`, border: `1px solid color-mix(in srgb, ${f.color} 22%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'flex-start' }}>
                    <f.icon style={{ width: 20, height: 20, color: f.color }} />
                  </motion.div>
                  <div>
                    <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#fff', margin: '0 0 8px' }}>{f.title}</h3>
                    <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.35)', margin: 0, lineHeight: 1.65 }}>{f.desc}</p>
                  </div>
                  <div style={{ marginTop: 'auto' }}>
                    <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', marginBottom: 14 }} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                      {f.bullets.map(b => (
                        <div key={b} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                          <CheckMark />
                          <span style={{ fontSize: '0.79rem', color: 'rgba(255,255,255,0.3)', lineHeight: 1.45 }}>{b}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </SpotlightCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Plans ── */}
      <section id="lp-plans" style={{ padding: '20px 20px 80px', background: '#0A0A12', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ maxWidth: 960, margin: '0 auto', paddingTop: 60 }}>
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, ease: EASE }}
            style={{ textAlign: 'center', marginBottom: 44 }}>
            <Eyebrow>Pricing</Eyebrow>
            <h2 style={{ fontSize: 'clamp(1.6rem, 4vw, 2.2rem)', fontWeight: 800, letterSpacing: '-0.025em', color: '#fff', margin: '0 0 10px' }}>Simple, Transparent Pricing</h2>
            <p style={{ color: 'rgba(255,255,255,0.32)', margin: 0 }}>Cancel anytime. No hidden fees. Start free.</p>
          </motion.div>

          <div className="lp-plans">
            {/* Free */}
            <motion.div initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, ease: EASE }} style={{ height: '100%' }}>
              <SpotlightCard color={BRAND} style={{ background: '#0F0F17', border: '1px solid rgba(255,255,255,0.08)', padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 24, height: '100%' }}>
                <div>
                  <p style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase', fontFamily: MONO, margin: '0 0 12px' }}>Free</p>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontSize: '2.2rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.04em', fontFamily: MONO }}>$0</span>
                    <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.28)' }}>/month</span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
                  {['1 contract analysis/month', '2 caption generations/month', 'Basic NIL Score', 'Community support'].map(f => (
                    <div key={f} style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}><CheckMark /><span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.38)', lineHeight: 1.45 }}>{f}</span></div>
                  ))}
                </div>
                <GhostBtn onClick={() => handleAuth(null, 'signup')}>Get Started Free</GhostBtn>
              </SpotlightCard>
            </motion.div>

            {/* Pro */}
            <motion.div initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, ease: EASE, delay: 0.08 }}
              style={{ position: 'relative', height: '100%' }}>
              {/* Popular badge */}
              <div style={{ position: 'absolute', top: -1, left: '50%', transform: 'translateX(-50%)', zIndex: 10, background: `linear-gradient(135deg, ${BRAND}, ${BRAND_2})`, borderRadius: '0 0 8px 8px', padding: '3px 14px', fontSize: '0.58rem', fontWeight: 800, color: '#fff', letterSpacing: '0.09em', whiteSpace: 'nowrap', fontFamily: MONO, boxShadow: '0 4px 12px rgba(90,56,253,0.4)' }}>MOST POPULAR</div>
              <SpotlightCard color={BRAND} style={{ background: `linear-gradient(180deg, rgba(90,56,253,0.13) 0%, rgba(90,56,253,0.04) 100%)`, border: '1px solid rgba(90,56,253,0.38)', padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 24, boxShadow: '0 0 40px rgba(90,56,253,0.12)', height: '100%' }}>
                <div>
                  <p style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.1em', color: BRAND_L, textTransform: 'uppercase', fontFamily: MONO, margin: '8px 0 12px' }}>Pro</p>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontSize: '2.2rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.04em', fontFamily: MONO }}>$9.99</span>
                    <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.28)' }}>/month</span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
                  {['5 contract analyses/month', '10 caption generations/month', 'Ghost Agent (4 chats/month)', 'Deal Safety Score™', 'Social Media Risk Scanner', 'NIL Earnings Tracker', 'Email support'].map(f => (
                    <div key={f} style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}><CheckMark /><span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.45 }}>{f}</span></div>
                  ))}
                </div>
                <PrimaryBtn onClick={() => handleAuth('pro', 'signup')}>Choose Pro →</PrimaryBtn>
              </SpotlightCard>
            </motion.div>

            {/* Elite */}
            <motion.div initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, ease: EASE, delay: 0.15 }} style={{ height: '100%', position: 'relative' }}>
              {/* Ambient elite glow — pulses subtly */}
              <motion.div
                animate={{ opacity: [0.18, 0.32, 0.18] }}
                transition={{ repeat: Infinity, duration: 4, ease: 'easeInOut' }}
                style={{ position: 'absolute', inset: -12, borderRadius: 26, background: 'radial-gradient(ellipse at center, rgba(127,0,255,0.28) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 }}
              />
              <SpotlightCard color={BRAND_2} style={{ position: 'relative', zIndex: 1, background: 'linear-gradient(145deg, rgba(127,0,255,0.16) 0%, rgba(90,56,253,0.08) 60%, rgba(90,56,253,0.02) 100%)', border: '1px solid rgba(127,0,255,0.45)', padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 24, height: '100%', boxShadow: '0 0 50px rgba(127,0,255,0.14), inset 0 0 0 1px rgba(127,0,255,0.08)' }}>
                <div>
                  <p style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.1em', color: '#fbbf24', textTransform: 'uppercase', fontFamily: MONO, margin: '0 0 12px' }}>Elite</p>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontSize: '2.2rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.04em', fontFamily: MONO }}>$21.99</span>
                    <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.28)' }}>/month</span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
                  {['Unlimited contract analyses', 'Unlimited captions', 'Ghost Agent (unlimited)', 'Advanced Social Scanner', 'Priority support', 'Advanced NIL Analytics', 'Market Value Assessment', 'Deal Performance Tracking'].map(f => (
                    <div key={f} style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}><CheckMark /><span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.45 }}>{f}</span></div>
                  ))}
                </div>
                <PrimaryBtn onClick={() => handleAuth('elite', 'signup')}>Choose Elite →</PrimaryBtn>
              </SpotlightCard>
            </motion.div>
          </div>

          <p style={{ textAlign: 'center', fontSize: '0.7rem', color: 'rgba(255,255,255,0.16)', marginTop: 18, fontFamily: MONO }}>Cancel anytime · Billed monthly via Stripe · No hidden fees</p>
        </div>
      </section>

      {/* ── Trust ── */}
      <section style={{ padding: '80px 20px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
            {[
              { Icon: Icons.Trophy,    color: BRAND_L, title: 'Built by Athletes',     body: "Former D1 athletes and senior engineers. We've been on the field — we know what student-athletes actually need." },
              { Icon: Icons.Shield,    color: BRAND_L, title: 'Secure by Design',      body: 'Bank-level encryption, secure AWS infrastructure, and privacy-first data practices protect every contract.' },
              { Icon: Icons.Instagram, color: BRAND_L, title: 'Official Integrations', body: 'Instagram Graph API only. No password sharing, no workarounds. Your account stays 100% safe.' },
            ].map((t, i) => (
              <motion.div key={t.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.42, ease: EASE, delay: i * 0.09 }}>
                <SpotlightCard color={t.color} style={{ background: '#0F0F17', border: '1px solid rgba(255,255,255,0.07)', padding: '20px' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 11, marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `color-mix(in srgb, ${t.color} 14%, rgba(0,0,0,0))`, border: `1px solid color-mix(in srgb, ${t.color} 22%, transparent)` }}>
                    <t.Icon style={{ width: 19, height: 19, color: t.color }} />
                  </div>
                  <h4 style={{ fontWeight: 700, color: 'rgba(255,255,255,0.88)', margin: '0 0 7px', fontSize: '0.92rem' }}>{t.title}</h4>
                  <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem', margin: 0, lineHeight: 1.65 }}>{t.body}</p>
                </SpotlightCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={{ padding: '20px 20px 80px', background: '#0A0A12', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ maxWidth: 660, margin: '0 auto', paddingTop: 60 }}>
          <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, ease: EASE }}
            style={{ textAlign: 'center', marginBottom: 32 }}>
            <h2 style={{ fontSize: 'clamp(1.5rem, 3.5vw, 2rem)', fontWeight: 800, letterSpacing: '-0.025em', color: '#fff', margin: 0 }}>Frequently Asked Questions</h2>
          </motion.div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {faqs.map(f => <FaqItem key={f.q} {...f} />)}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section style={{ padding: '90px 20px', position: 'relative', overflow: 'hidden' }}>
        {/* Pulsing background glow */}
        <motion.div
          animate={{ scale: [1, 1.12, 1], opacity: [0.15, 0.25, 0.15] }}
          transition={{ repeat: Infinity, duration: 6, ease: 'easeInOut' }}
          style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 700, height: 500, background: `radial-gradient(ellipse at center, rgba(90,56,253,0.22) 0%, transparent 70%)`, pointerEvents: 'none', borderRadius: '50%' }}
        />
        <div style={{ position: 'relative', maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
          <motion.div initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.65, ease: EASE }}>
            <h2 style={{ fontSize: 'clamp(1.8rem, 4.5vw, 2.6rem)', fontWeight: 900, letterSpacing: '-0.035em', margin: '0 0 14px',
              background: 'linear-gradient(135deg, #fff 0%, #c4b5fd 50%, #93c5fd 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Ready to Grow Your NIL the Smart Way?
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.35)', marginBottom: 36, fontSize: '1rem', lineHeight: 1.65 }}>
              Join the growing community of student-athletes protecting their deals and building their brand with NILVISION.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
              <PrimaryBtn large onClick={() => handleAuth(null, 'signup')}>Start Your Free Account →</PrimaryBtn>
              <GhostBtn large onClick={() => document.getElementById('lp-plans')?.scrollIntoView({ behavior: 'smooth' })}>Compare Plans</GhostBtn>
            </div>
            <p style={{ marginTop: 18, fontSize: '0.72rem', color: 'rgba(255,255,255,0.18)', fontFamily: MONO }}>No credit card to start · Cancel anytime</p>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{ background: '#08080D', borderTop: '1px solid rgba(255,255,255,0.06)', padding: '36px 20px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src="/nilvision-logo-gradient-background.png" alt="NILVision" style={{ width: 26, height: 26, borderRadius: 6 }} />
                <span style={{ fontSize: '1rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', letterSpacing: '-0.01em' }}>NILVISION</span>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                {[{ href: 'https://www.instagram.com/nilvisionpro', Icon: Icons.Instagram }, { href: 'https://x.com/nilvisionpro', Icon: Icons.Twitter }].map(({ href, Icon }) => (
                  <a key={href} href={href} target="_blank" rel="noopener noreferrer"
                    style={{ color: 'rgba(255,255,255,0.2)', transition: 'color 160ms' }}
                    onMouseEnter={e => e.currentTarget.style.color = BRAND_L}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.2)'}>
                    <Icon style={{ width: 17, height: 17 }} />
                  </a>
                ))}
              </div>
            </div>
            <nav style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', fontSize: '0.72rem', fontFamily: MONO }}>
              {[['FAQ', '/legal/faq'], ['Privacy', '/legal/privacy'], ['Terms', '/legal/terms'], ['Data Deletion', '/legal/data-deletion'], ['Contact', 'mailto:info@nilvisionpro.com']].map(([label, href]) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                  style={{ color: 'rgba(255,255,255,0.2)', textDecoration: 'none', transition: 'color 160ms' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.2)'}>{label}</a>
              ))}
            </nav>
          </div>
          <p style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.12)', margin: 0, fontFamily: MONO, textAlign: 'right' }}>© 2025 NILVISION. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
